// pages/AIActionsPage.jsx — AI Actions: perintah bahasa natural → aksi terkontrol.
// ─────────────────────────────────────────────────────────────────────────────
// Alur: ketik perintah → AI (planner) memilih aksi + parameter → tampil dry-run
// → Approve/Tolak (aksi write/critical) → hasil + riwayat audit.
// Backend: /api/ai/actions/propose, /:id/decide, /defs, /recent.
// Pola mengikuti halaman lain: axios dari AuthContext (token otomatis), Tailwind.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useEffect, useCallback } from 'react';
import { api as axiosApi } from '../context/AuthContext';
import { useSearchParams } from 'react-router-dom';
import { useLang } from '../context/LanguageContext';
import useTabDeepLink from '../hooks/useTabDeepLink';
import {
  Zap, Send, ShieldCheck, ShieldAlert, CheckCircle2, XCircle, Clock,
  RefreshCw, ChevronDown, ChevronUp, Loader2, Bot,
  Plus, Trash2, Wrench, Database, X,
} from 'lucide-react';

// Tab yang boleh datang dari `?tab=` (QuickSearch).
const AIACT_TABS = ['run', 'manage'];

// Badge kecil untuk status/risk.
const RISK_STYLE = {
  read:     'bg-emerald-50 text-emerald-700',
  write:    'bg-amber-50 text-amber-700',
  critical: 'bg-rose-50 text-rose-700',
};
const STATUS_STYLE = {
  executed: 'bg-emerald-50 text-emerald-700',
  proposed: 'bg-amber-50 text-amber-700',
  approved: 'bg-sky-50 text-sky-700',
  rejected: 'bg-slate-100 text-slate-600',
  failed:   'bg-rose-50 text-rose-700',
  expired:  'bg-slate-100 text-slate-500',
};
function Badge({ value, map }) {
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${map[value] || 'bg-slate-100 text-slate-600'}`}>
      {value}
    </span>
  );
}

// ── Render hasil eksekusi ────────────────────────────────────────────────────
// Prioritas: bila hasil punya array-of-object (mis. `rows`), tampilkan sebagai
// TABEL — jauh lebih terbaca di HP daripada JSON mentah. Key skalar lain
// (count, target, truncated) jadi chip ringkasan di atas tabel. JSON mentah
// tetap tersedia lewat toggle, karena untuk debugging bentuk asli masih perlu.
// Bentuk yang tidak tabular jatuh ke JSON rapi + tombol copy.

// Tabular = array tak kosong yang seluruh elemennya objek biasa (bukan array).
function isTabular(v) {
  return Array.isArray(v) && v.length > 0
    && v.every(r => r && typeof r === 'object' && !Array.isArray(r));
}

// Union semua key dari maks 200 baris pertama — baris bisa punya key berbeda,
// jadi header tidak boleh diambil dari baris pertama saja.
function columnsOf(rows) {
  const seen = [];
  rows.slice(0, 200).forEach(r => Object.keys(r).forEach(k => { if (!seen.includes(k)) seen.push(k); }));
  return seen;
}

function cellText(v) {
  if (v == null) return '—';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

const MAX_TABLE_ROWS = 50;

function DataTable({ rows }) {
  const { t } = useLang();
  const cols = columnsOf(rows);
  const shown = rows.slice(0, MAX_TABLE_ROWS);
  return (
    <div className="mt-2">
      <div className="overflow-x-auto rounded-lg border border-slate-200">
        <table className="min-w-full text-xs">
          <thead className="bg-slate-50">
            <tr>
              {cols.map(c => (
                <th key={c} className="text-left font-semibold text-slate-600 px-2.5 py-2 whitespace-nowrap">{c}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {shown.map((r, i) => (
              <tr key={i} className={i % 2 ? 'bg-slate-50/60' : 'bg-white'}>
                {cols.map(c => (
                  <td key={c} className="px-2.5 py-1.5 text-slate-700 whitespace-nowrap font-mono">{cellText(r[c])}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows.length > shown.length && (
        <div className="mt-1 text-xs text-slate-400">{t('aiact.moreItems', { n: rows.length - shown.length })}</div>
      )}
    </div>
  );
}

// Chip ringkasan untuk key skalar di sisi objek hasil.
function SummaryChips({ pairs }) {
  if (!pairs.length) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-1.5">
      {pairs.map(([k, v]) => (
        <span key={k} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-xs text-slate-600">
          <span className="text-slate-400">{k}</span>
          <span className="font-medium text-slate-700">{cellText(v)}</span>
        </span>
      ))}
    </div>
  );
}

// JSON mentah + copy. Dipakai sebagai fallback maupun isi toggle.
function RawJson({ value }) {
  const { t } = useLang();
  const [copied, setCopied] = useState(false);
  const text = JSON.stringify(value, null, 2);
  const copy = () => {
    try {
      navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_) {}
  };
  return (
    <div className="mt-2 relative">
      <button onClick={copy}
        className="absolute top-1.5 right-1.5 px-2 py-0.5 rounded-md bg-white/90 border border-slate-200 text-xs text-slate-500 hover:text-slate-700">
        {copied ? t('aiact.copied') : t('aiact.copy')}
      </button>
      <pre className="text-xs bg-slate-50 rounded-lg p-3 pt-7 overflow-x-auto text-slate-700">{text}</pre>
    </div>
  );
}

function ResultView({ result }) {
  const { t } = useLang();
  const [showJson, setShowJson] = useState(false);
  if (result == null) return null;

  // Kasus khusus dipertahankan lebih dulu — password sementara & error messages
  // punya perlakuan visual sendiri dan tidak boleh jatuh ke tabel/JSON.
  if (typeof result === 'object' && !Array.isArray(result)) {
    if ('newPassword' in result) {
      return (
        <div className="mt-2 text-sm">
          {result.success !== false ? (
            <div className="bg-violet-50 border border-violet-200 rounded-xl px-4 py-3">
              <div className="text-violet-800 font-medium">{t('aiact.tempPassword')}</div>
              <code className="text-lg font-mono text-violet-900">{result.newPassword}</code>
              <div className="text-xs text-violet-600 mt-1">{t('aiact.tempPasswordHint')}</div>
            </div>
          ) : (
            <div className="text-rose-600">{JSON.stringify(result.messages || result)}</div>
          )}
        </div>
      );
    }
    if (result.success === false && result.messages) {
      return (
        <div className="mt-2 text-sm text-rose-600">
          {result.messages.map((m, i) => <div key={i}>❌ {m.message || JSON.stringify(m)}</div>)}
        </div>
      );
    }
  }

  const JsonToggle = () => (
    <button onClick={() => setShowJson(s => !s)}
      className="mt-2 inline-flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600">
      {showJson ? <ChevronUp style={{ width: 13, height: 13 }} /> : <ChevronDown style={{ width: 13, height: 13 }} />}
      {showJson ? t('aiact.hideJson') : t('aiact.showJson')}
    </button>
  );

  // (1) Hasil berupa array-of-object langsung → tabel.
  if (isTabular(result)) {
    return (
      <div>
        <DataTable rows={result} />
        <JsonToggle />
        {showJson && <RawJson value={result} />}
      </div>
    );
  }

  // (2) Array biasa (skalar / campuran) → daftar ringkas, seperti sebelumnya.
  if (Array.isArray(result)) {
    return (
      <ul className="mt-2 space-y-1">
        {result.slice(0, 30).map((r, i) => (
          <li key={i} className="text-sm text-slate-700 bg-slate-50 rounded-lg px-3 py-1.5">
            {typeof r === 'object' ? Object.values(r).join(' — ') : String(r)}
          </li>
        ))}
        {result.length > 30 && <li className="text-xs text-slate-400">{t('aiact.moreItems', { n: result.length - 30 })}</li>}
      </ul>
    );
  }

  if (typeof result === 'object') {
    // Cari array-of-object di dalam objek: `rows` diutamakan, lalu key lain.
    const entries = Object.entries(result);
    const tabularKey = isTabular(result.rows)
      ? 'rows'
      : (entries.find(([, v]) => isTabular(v)) || [null])[0];

    if (tabularKey) {
      const scalars = entries.filter(([k, v]) =>
        k !== tabularKey && (v == null || typeof v !== 'object'));
      // Key objek/array lain selain tabel utama: jangan disembunyikan diam-diam —
      // biarkan pengguna menemukannya lewat JSON.
      return (
        <div>
          <SummaryChips pairs={scalars} />
          <DataTable rows={result[tabularKey]} />
          <JsonToggle />
          {showJson && <RawJson value={result} />}
        </div>
      );
    }

    return <RawJson value={result} />;
  }

  return <div className="mt-2 text-sm text-slate-700">{String(result)}</div>;
}

export default function AIActionsPage() {
  const { t } = useLang();
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(() => {
    const p = searchParams.get('tab');
    return AIACT_TABS.includes(p) ? p : 'run';
  });   // 'run' | 'manage'
  useTabDeepLink('ai_actions', (p) => AIACT_TABS.includes(p), setTab);
  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-2xl bg-violet-50 flex items-center justify-center">
          <Zap className="text-violet-500" size={22} />
        </div>
        <div>
          <h1 className="font-display text-xl font-bold text-slate-800">{t('aiact.title')}</h1>
          <p className="text-sm text-slate-500">{t('aiact.subtitle')}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-200">
        {[['run', t('aiact.tabRun'), Send], ['manage', t('aiact.tabManage'), Wrench]].map(([k, label, Icon]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            className={`px-4 py-2.5 text-sm font-medium flex items-center gap-2 border-b-2 -mb-px transition ${
              tab === k ? 'border-violet-500 text-violet-600' : 'border-transparent text-slate-400 hover:text-slate-600'
            }`}
          >
            <Icon size={15} /> {label}
          </button>
        ))}
      </div>

      {tab === 'run' ? <RunTab /> : <ManageTab />}
    </div>
  );
}

function RunTab() {
  const { t } = useLang();
  const [text, setText]         = useState('');
  const [busy, setBusy]         = useState(false);
  const [deciding, setDeciding] = useState(false);
  const [current, setCurrent]   = useState(null);   // { plan, prop, decided }
  const [error, setError]       = useState('');
  const [recent, setRecent]     = useState([]);
  const [defs, setDefs]         = useState([]);
  const [showDefs, setShowDefs] = useState(false);

  const loadRecent = useCallback(async () => {
    try {
      const res = await axiosApi.get('/api/ai/actions/recent?hours=48');
      setRecent(Array.isArray(res.data) ? res.data : []);
    } catch (_) { /* riwayat opsional; jangan ganggu halaman */ }
  }, []);

  useEffect(() => {
    loadRecent();
    axiosApi.get('/api/ai/actions/defs')
      .then(res => setDefs(Array.isArray(res.data) ? res.data : []))
      .catch(() => {});
  }, [loadRecent]);

  async function submit() {
    const q = text.trim();
    if (!q || busy) return;
    setBusy(true); setError(''); setCurrent(null);
    try {
      const res = await axiosApi.post('/api/ai/actions/propose', { text: q });
      setCurrent({ ...res.data, decided: null });
      if (res.data?.prop?.status === 'executed') loadRecent();
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally { setBusy(false); }
  }

  async function decide(approve) {
    const id = current?.prop?.id;
    if (!id || deciding) return;
    setDeciding(true); setError('');
    try {
      const res = await axiosApi.post(`/api/ai/actions/${id}/decide`, { approve });
      setCurrent(c => ({ ...c, decided: res.data }));
      loadRecent();
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally { setDeciding(false); }
  }

  const prop = current?.prop;
  const plan = current?.plan;
  const decided = current?.decided;
  const awaiting = prop && prop.status === 'proposed' && !decided;

  return (
    <div className="space-y-6">
      {/* Input */}
      <div className="bg-white rounded-2xl border border-slate-200 p-4">
        <div className="flex gap-2">
          <input
            className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300"
            placeholder={t('aiact.inputPlaceholder')}
            value={text}
            onChange={e => setText(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') submit(); }}
            disabled={busy}
          />
          <button
            onClick={submit}
            disabled={busy || !text.trim()}
            className="px-4 py-2.5 rounded-xl bg-violet-600 text-white text-sm font-medium hover:bg-violet-700 disabled:opacity-40 flex items-center gap-2"
          >
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            {t('aiact.send')}
          </button>
        </div>

        {/* Daftar aksi tersedia (collapsible) */}
        <button
          onClick={() => setShowDefs(s => !s)}
          className="mt-3 text-xs text-slate-400 hover:text-slate-600 flex items-center gap-1"
        >
          {showDefs ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          {t('aiact.actionsAvailable', { count: defs.length })}
        </button>
        {showDefs && (
          <div className="mt-2 grid gap-1.5">
            {defs.map(d => (
              <div key={d.name} className="flex items-start gap-2 text-xs bg-slate-50 rounded-lg px-3 py-2">
                <Badge value={d.risk} map={RISK_STYLE} />
                <div>
                  <span className="font-mono text-slate-700">{d.name}</span>
                  <span className="text-slate-500"> — {d.description}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {error && (
        <div className="bg-rose-50 border border-rose-200 text-rose-700 text-sm rounded-xl px-4 py-3">
          {error}
        </div>
      )}

      {/* Hasil propose */}
      {current && (
        <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3">
          {/* Rencana AI */}
          {plan && (
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-xl bg-indigo-50 flex items-center justify-center shrink-0">
                <Bot className="text-indigo-500" size={16} />
              </div>
              <div className="text-sm">
                {plan.action ? (
                  <>
                    <div className="text-slate-800">
                      {t('aiact.aiChose')} <span className="font-mono font-medium">{plan.action}</span>
                      {plan.params && Object.keys(plan.params).length > 0 && (
                        <span className="text-slate-500"> {t('aiact.with')} {Object.entries(plan.params).map(([k, v]) => `${k}=${v}`).join(', ')}</span>
                      )}
                    </div>
                    {plan.reason && <div className="text-slate-400 text-xs mt-0.5">{plan.reason}</div>}
                  </>
                ) : (
                  <div className="text-slate-600">{plan.reason || t('aiact.noMatch')}</div>
                )}
              </div>
            </div>
          )}

          {/* Proposal / hasil */}
          {prop && prop.error && (
            <div className="text-sm text-rose-600 bg-rose-50 rounded-xl px-4 py-3">{prop.error}</div>
          )}

          {prop && prop.status === 'executed' && !decided && (
            <div>
              <div className="flex items-center gap-2 text-sm text-emerald-700">
                <CheckCircle2 size={16} /> {t('aiact.executedDirect')} <code className="text-xs">#{prop.id}</code>
              </div>
              <ResultView result={prop.result} />
            </div>
          )}

          {awaiting && (
            <div className="border border-amber-200 bg-amber-50 rounded-xl p-4">
              <div className="flex items-center gap-2 text-amber-800 text-sm font-medium">
                <ShieldAlert size={16} />
                {t('aiact.awaitingApproval')} <code className="text-xs">#{prop.id}</code>
                <Badge value={prop.risk} map={RISK_STYLE} />
              </div>
              <div className="text-sm text-amber-900 mt-2 font-mono bg-white/60 rounded-lg px-3 py-2">
                {prop.dryRun?.plan || prop.action}
              </div>
              <div className="text-xs text-amber-600 mt-1">{t('aiact.expiryHint')}</div>
              <div className="flex gap-2 mt-3">
                <button
                  onClick={() => decide(true)}
                  disabled={deciding}
                  className="px-4 py-2 rounded-xl bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-40 flex items-center gap-1.5"
                >
                  {deciding ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />} {t('aiact.approveRun')}
                </button>
                <button
                  onClick={() => decide(false)}
                  disabled={deciding}
                  className="px-4 py-2 rounded-xl bg-white border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-50 disabled:opacity-40 flex items-center gap-1.5"
                >
                  <XCircle size={14} /> {t('aiact.reject')}
                </button>
              </div>
            </div>
          )}

          {decided && (
            <div>
              {decided.status === 'executed' && (
                <div className="flex items-center gap-2 text-sm text-emerald-700">
                  <CheckCircle2 size={16} /> {t('aiact.approvedExecuted')}
                </div>
              )}
              {decided.status === 'rejected' && (
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <XCircle size={16} /> {t('aiact.rejected')}
                </div>
              )}
              {decided.status === 'failed' && (
                <div className="flex items-center gap-2 text-sm text-rose-600">
                  <XCircle size={16} /> {t('aiact.execFailed')} {decided.error}
                </div>
              )}
              {decided.error && !decided.status && (
                <div className="text-sm text-rose-600">{decided.error}</div>
              )}
              <ResultView result={decided.result} />
            </div>
          )}
        </div>
      )}

      {/* Riwayat */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
            <Clock size={15} className="text-slate-400" /> {t('aiact.history48h')}
          </h2>
          <button onClick={loadRecent} className="text-slate-400 hover:text-slate-600" title={t('common.refresh')}>
            <RefreshCw size={15} />
          </button>
        </div>
        {recent.length === 0 ? (
          <div className="text-sm text-slate-400 py-6 text-center">{t('aiact.noActionsYet')}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-slate-400 border-b border-slate-100">
                  <th className="py-2 pr-3 font-medium">{t('aiact.colTime')}</th>
                  <th className="py-2 pr-3 font-medium">{t('common.actions')}</th>
                  <th className="py-2 pr-3 font-medium">{t('aiact.colParam')}</th>
                  <th className="py-2 pr-3 font-medium">{t('aiact.colSource')}</th>
                  <th className="py-2 pr-3 font-medium">{t('aiact.colRequester')}</th>
                  <th className="py-2 pr-3 font-medium">{t('aiact.colStatus')}</th>
                  <th className="py-2 font-medium">{t('aiact.colApprover')}</th>
                </tr>
              </thead>
              <tbody>
                {recent.map(r => (
                  <tr key={r.id} className="border-b border-slate-50 hover:bg-slate-50/60">
                    <td className="py-2 pr-3 text-slate-500 whitespace-nowrap text-xs">{r.created_at}</td>
                    <td className="py-2 pr-3 font-mono text-xs text-slate-700">{r.action_name}</td>
                    <td className="py-2 pr-3 font-mono text-xs text-slate-500 max-w-[180px] truncate" title={r.params_json}>{r.params_json}</td>
                    <td className="py-2 pr-3 text-xs text-slate-500">{r.source}</td>
                    <td className="py-2 pr-3 text-xs text-slate-500">{r.requested_by || '—'}</td>
                    <td className="py-2 pr-3"><Badge value={r.status} map={STATUS_STYLE} /></td>
                    <td className="py-2 text-xs text-slate-500">{r.approver || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Tab: Kelola Aksi Custom (SQL read-only, dibuat lewat UI) ─────────────────
const EMPTY_FORM = { name: '', description: '', sql_template: '', allow_tables: [], params: [], max_rows: 100 };

function ManageTab() {
  const { t } = useLang();
  const [list, setList]       = useState([]);
  const [tables, setTables]   = useState([]);
  const [form, setForm]       = useState(EMPTY_FORM);
  const [editing, setEditing] = useState(false);
  const [error, setError]     = useState('');
  const [saving, setSaving]   = useState(false);

  const load = useCallback(async () => {
    try {
      const [c, s] = await Promise.all([
        axiosApi.get('/api/ai/actions/custom'),
        axiosApi.get('/api/ai/actions/schema'),
      ]);
      setList(Array.isArray(c.data) ? c.data : []);
      setTables(s.data?.tables || []);
    } catch (e) { setError(e?.response?.data?.error || e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  function reset() { setForm(EMPTY_FORM); setEditing(false); setError(''); }

  function editRow(row) {
    setForm({
      name: row.name,
      description: (row.description || '').replace(/ \[custom\]$/, ''),
      sql_template: row.sql_template,
      allow_tables: JSON.parse(row.allow_tables || '[]'),
      params: JSON.parse(row.params_json || '[]'),
      max_rows: row.max_rows || 100,
    });
    setEditing(true); setError('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  async function save() {
    setSaving(true); setError('');
    try {
      await axiosApi.post('/api/ai/actions/custom', form);
      reset(); load();
    } catch (e) { setError(e?.response?.data?.error || e.message); }
    finally { setSaving(false); }
  }

  async function del(name) {
    if (!window.confirm(t('aiact.confirmDelete', { name }))) return;
    try { await axiosApi.delete(`/api/ai/actions/custom/${name}`); load(); }
    catch (e) { setError(e?.response?.data?.error || e.message); }
  }

  // Placeholder :param di SQL → auto-usulkan sebagai parameter.
  const detectedParams = Array.from(new Set((form.sql_template.match(/:([a-zA-Z_][a-zA-Z0-9_]*)/g) || []).map(s => s.slice(1))));

  function setParam(name, patch) {
    setForm(f => {
      const exists = f.params.find(p => p.name === name);
      const params = exists
        ? f.params.map(p => p.name === name ? { ...p, ...patch } : p)
        : [...f.params, { name, type: 'string', required: true, ...patch }];
      return { ...f, params };
    });
  }
  function paramMeta(name) { return form.params.find(p => p.name === name) || { type: 'string', required: true }; }

  function toggleTable(t) {
    setForm(f => ({
      ...f,
      allow_tables: f.allow_tables.includes(t) ? f.allow_tables.filter(x => x !== t) : [...f.allow_tables, t],
    }));
  }

  return (
    <div className="space-y-6">
      {/* Form builder */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
            <Plus size={15} className="text-violet-500" /> {editing ? t('aiact.editForm', { name: form.name }) : t('aiact.newAction')}
          </h2>
          {editing && (
            <button onClick={reset} className="text-xs text-slate-400 hover:text-slate-600 flex items-center gap-1">
              <X size={13} /> {t('aiact.cancelEdit')}
            </button>
          )}
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium text-slate-500">{t('aiact.lblName')}</label>
            <input
              className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-violet-300 disabled:bg-slate-50"
              placeholder="open_solman_tickets"
              value={form.name}
              disabled={editing}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500">{t('aiact.lblMaxRows')}</label>
            <input
              type="number"
              className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300"
              value={form.max_rows}
              onChange={e => setForm(f => ({ ...f, max_rows: Number(e.target.value) }))}
            />
          </div>
        </div>

        <div>
          <label className="text-xs font-medium text-slate-500">{t('aiact.lblDescription')}</label>
          <input
            className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300"
            placeholder={t('aiact.descPlaceholder')}
            value={form.description}
            onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
          />
        </div>

        <div>
          <label className="text-xs font-medium text-slate-500">{t('aiact.lblSql')}</label>
          <textarea
            rows={3}
            className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-violet-300"
            placeholder="SELECT id, subject, status FROM solman_tickets WHERE status = :status LIMIT 50"
            value={form.sql_template}
            onChange={e => setForm(f => ({ ...f, sql_template: e.target.value }))}
          />
        </div>

        {/* Parameter terdeteksi */}
        {detectedParams.length > 0 && (
          <div>
            <label className="text-xs font-medium text-slate-500">{t('aiact.detectedParams')}</label>
            <div className="mt-1 space-y-2">
              {detectedParams.map(name => {
                const meta = paramMeta(name);
                return (
                  <div key={name} className="flex items-center gap-3 text-sm bg-slate-50 rounded-lg px-3 py-2">
                    <code className="font-mono text-violet-700">:{name}</code>
                    <select
                      className="text-xs border border-slate-200 rounded px-2 py-1"
                      value={meta.type}
                      onChange={e => setParam(name, { type: e.target.value })}
                    >
                      <option value="string">string</option>
                      <option value="number">number</option>
                    </select>
                    <label className="text-xs text-slate-500 flex items-center gap-1">
                      <input type="checkbox" checked={meta.required !== false} onChange={e => setParam(name, { required: e.target.checked })} />
                      {t('aiact.required')}
                    </label>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Allowlist tabel */}
        <div>
          <label className="text-xs font-medium text-slate-500 flex items-center gap-1">
            <Database size={13} /> {t('aiact.lblTables')}
          </label>
          <div className="mt-1 flex flex-wrap gap-1.5 max-h-32 overflow-y-auto p-1">
            {tables.map(t => (
              <button
                key={t}
                onClick={() => toggleTable(t)}
                className={`px-2 py-1 rounded-lg text-xs font-mono border transition ${
                  form.allow_tables.includes(t)
                    ? 'bg-violet-50 border-violet-300 text-violet-700'
                    : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300'
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        {error && <div className="text-sm text-rose-600 bg-rose-50 rounded-lg px-3 py-2">{error}</div>}

        <button
          onClick={save}
          disabled={saving || !form.name || !form.description || !form.sql_template || !form.allow_tables.length}
          className="px-4 py-2 rounded-xl bg-violet-600 text-white text-sm font-medium hover:bg-violet-700 disabled:opacity-40 flex items-center gap-2"
        >
          {saving ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />}
          {editing ? t('aiact.saveChanges') : t('aiact.createAction')}
        </button>
      </div>

      {/* Daftar custom action */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <h2 className="text-sm font-semibold text-slate-700 mb-3">{t('aiact.customActions')} ({list.length})</h2>
        {list.length === 0 ? (
          <div className="text-sm text-slate-400 py-6 text-center">{t('aiact.noCustomActions')}</div>
        ) : (
          <div className="space-y-2">
            {list.map(row => (
              <div key={row.name} className="flex items-start justify-between gap-3 border border-slate-100 rounded-xl px-4 py-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <code className="font-mono text-sm text-slate-700">custom_{row.name}</code>
                    {!row.enabled && <span className="text-xs text-slate-400">{t('aiact.disabled')}</span>}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">{(row.description || '').replace(/ \[custom\]$/, '')}</div>
                  <div className="text-xs text-slate-400 font-mono mt-1 truncate">{row.sql_template}</div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button onClick={() => editRow(row)} className="p-1.5 text-slate-400 hover:text-violet-600" title={t('common.edit')}><Wrench size={15} /></button>
                  <button onClick={() => del(row.name)} className="p-1.5 text-slate-400 hover:text-rose-600" title={t('common.delete')}><Trash2 size={15} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
